import { ReactNode } from 'react';

import { InfoIcon } from 'lucide-react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { SingleSelect, SingleSelectOption } from '@/modules/common';
import { DecimalInput } from '@/modules/common/components/molecules/inputs/decimal-form-field';
import { RateSource } from '@/modules/common/types/rate-source';
import { RateMask } from '@/modules/common/utils/rate-mask';
import { isNumber } from '@/utils/number/is-number';

import { MIN_RATE_NUMBER } from '../../../constants';
import {
  FIELD_CURRENCY,
  FIELD_RATE,
  FIELD_RATE_SOURCE,
} from '../../../constants/assignment-modal-constants';
import { renderRateSourceOption, renderRateSourceTrigger } from '../../../helpers';
import { RateOption } from '../../../types';
import { AssignmentFormValues } from './assignment-modal';

interface AssignmentModalRateFieldsProps {
  control: Control<AssignmentFormValues>;
  rateOptions: RateOption[];
  currencyOptions: SingleSelectOption[];
  isLoadingCurrencies: boolean;
  isLoadingRates: boolean;
  initialCurrencyItem: SingleSelectOption | undefined;
  isRateDisabledByBillable: boolean;
  /** Whether the rate-source dropdown can be opened at all (a member and project are chosen). */
  isRateSourceSelectable: boolean;
  /** Opening the dropdown lifts edit mode's deferral of the rate-options query. */
  onRateSourceDropdownOpen: (isOpen: boolean) => void;
  rateSource: RateSource | undefined;
  shouldShowRateSourceAlert: boolean;
  isInitialRateNonProjectBased: boolean;
  effectiveDateLabel: string | null;
  onCurrencyDropdownOpen: (open: boolean) => void;
  /** Read-only modal (AssignmentsViewAll without AssignmentsEdit) — disable every field. */
  isReadOnly?: boolean;
}

/**
 * Private helper that wraps a field in a billable-disabled tooltip when needed,
 * or renders it plainly otherwise. Eliminates the triple tooltip-wrapper pattern.
 */
function FieldWithBillableTooltip({
  isDisabled,
  tooltipContent,
  children,
}: {
  isDisabled: boolean;
  tooltipContent: string;
  children: (disabled: boolean) => ReactNode;
}) {
  if (isDisabled) {
    return (
      <SimpleTooltip content={tooltipContent}>
        <div className="min-w-0">{children(true)}</div>
      </SimpleTooltip>
    );
  }
  return <div className="min-w-0">{children(false)}</div>;
}

/**
 * Info icon for the Rate label. Rendered inline (Tailwind's preflight makes svg a block by
 * default) so it joins the label's existing line box instead of turning the label into a block.
 */
function RateLabelInfoIcon() {
  return <InfoIcon size={14} className="ml-1 inline align-text-bottom text-muted-foreground" />;
}

/**
 * Three-column rate grid (Rate Source / Rate / Currency) plus the rate-source alert.
 * Used exclusively by AssignmentModal.
 */
export function AssignmentModalRateFields({
  control,
  rateOptions,
  currencyOptions,
  isLoadingCurrencies,
  isLoadingRates,
  initialCurrencyItem,
  isRateDisabledByBillable,
  isRateSourceSelectable,
  onRateSourceDropdownOpen,
  rateSource,
  shouldShowRateSourceAlert,
  isInitialRateNonProjectBased,
  effectiveDateLabel,
  onCurrencyDropdownOpen,
  isReadOnly = false,
}: AssignmentModalRateFieldsProps) {
  const { t } = useTranslation('authenticated.planner');
  const billableTooltip = t('form.isBillable.rateDisabledTooltip');

  // When rates are loading, disable all rate fields on top of the billable-disabled state.
  // Read-only mode forces every rate field disabled regardless of billable state.
  const isDisabledByBillableOrLoading = isRateDisabledByBillable || isLoadingRates || isReadOnly;
  const isRateEditable = !isDisabledByBillableOrLoading && rateSource === RateSource.Custom;

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <div className="grid grid-cols-[7fr_3fr_3fr] gap-2">
          {/* Rate Source */}
          <FieldWithBillableTooltip
            isDisabled={isRateDisabledByBillable}
            tooltipContent={billableTooltip}
          >
            {disabled => (
              <SingleSelect
                name={FIELD_RATE_SOURCE}
                control={control}
                label={
                  disabled ? (
                    <span className="flex items-center gap-1">
                      {t('form.rateSource.label')}
                      <InfoIcon size={14} className="text-muted-foreground" />
                    </span>
                  ) : (
                    t('form.rateSource.label')
                  )
                }
                placeholder={t('form.rateSource.placeholder')}
                disabled={isDisabledByBillableOrLoading || !isRateSourceSelectable}
                searchable={false}
                options={rateOptions}
                onOpenChange={onRateSourceDropdownOpen}
                renderTrigger={renderRateSourceTrigger}
                renderOption={renderRateSourceOption}
                isClearSelectionEnabled={false}
              />
            )}
          </FieldWithBillableTooltip>

          {/* Rate */}
          <FieldWithBillableTooltip
            isDisabled={isRateDisabledByBillable}
            tooltipContent={billableTooltip}
          >
            {disabled => (
              <FormField
                control={control}
                name={FIELD_RATE}
                render={({ field }) => {
                  // The backend replaces rates this user may not see with a sentinel rather than
                  // omitting them, so the form value itself can be masked. Keep the sentinel in the
                  // form (buildCommonPayload omits it, which makes the backend keep the stored rate)
                  // and mask only what is rendered.
                  const isMaskedRate = RateMask.isMaskedStringValue(field.value);

                  return (
                    <FormItem>
                      {/* The icon is inline rather than wrapped in a flex box, and the label keeps
                          its default box: FormLabel renders an inline <label>, so its text sits in a
                          line box sized by the inherited 1.5 line-height, the same as the plain
                          Rate Source and Currency labels beside it. Making this one a flex (block)
                          box collapses it to the 14px text height and lifts it a few pixels above
                          its neighbours. */}
                      <FormLabel>
                        {t('form.rate.label')}
                        {disabled && <RateLabelInfoIcon />}
                        {!disabled && isMaskedRate && (
                          <SimpleTooltip
                            // Only offer "enter a new one" when the input actually accepts typing.
                            content={
                              isRateEditable
                                ? t('form.rate.maskedEditableTooltip')
                                : t('form.rate.maskedTooltip')
                            }
                            contentClassName="max-w-56"
                          >
                            <InfoIcon
                              size={14}
                              className="ml-1 inline align-text-bottom text-muted-foreground"
                            />
                          </SimpleTooltip>
                        )}
                      </FormLabel>
                      <FormControl>
                        <InputGroup disabled={!isRateEditable}>
                          {isRateEditable ? (
                            <DecimalInput
                              name={field.name}
                              // Never hand the raw sentinel to the input: DecimalInput clamps to
                              // minValue on blur, so a stray focus+blur would silently rewrite it to
                              // "0" — and a Custom rate of 0 makes buildCommonPayload force isBillable
                              // to false. Typing over the mask works because normalizeDecimalInput
                              // strips every non-digit.
                              value={
                                isMaskedRate
                                  ? RateMask.applyToString(field.value)
                                  : (field.value ?? '')
                              }
                              inputMode={isMaskedRate ? undefined : 'decimal'}
                              onValueChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder={t('form.rate.placeholder')}
                              maxDecimalPlaces={2}
                              minValue={MIN_RATE_NUMBER}
                            />
                          ) : (
                            <InputGroupInput
                              {...field}
                              value={
                                isNumber(field.value) ? RateMask.apply(parseFloat(field.value)) : ''
                              }
                              placeholder={t('form.rate.placeholder')}
                              autoComplete="off"
                              readOnly
                            />
                          )}
                        </InputGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}
          </FieldWithBillableTooltip>

          {/* Currency */}
          <FieldWithBillableTooltip
            isDisabled={isRateDisabledByBillable}
            tooltipContent={billableTooltip}
          >
            {disabled => (
              <SingleSelect
                name={FIELD_CURRENCY}
                control={control}
                label={
                  disabled ? (
                    <span className="flex items-center gap-1">
                      {t('form.currency.label')}
                      <InfoIcon size={14} className="text-muted-foreground" />
                    </span>
                  ) : (
                    t('form.currency.label')
                  )
                }
                placeholder={t('form.currency.placeholder')}
                disabled={isDisabledByBillableOrLoading || rateSource !== RateSource.Custom}
                searchable={!isDisabledByBillableOrLoading}
                loading={!isDisabledByBillableOrLoading && isLoadingCurrencies}
                options={currencyOptions}
                formItemClassName={disabled ? undefined : 'block'}
                className={disabled ? undefined : 'h-10'}
                initialSelectedItem={initialCurrencyItem}
                onOpenChange={isDisabledByBillableOrLoading ? undefined : onCurrencyDropdownOpen}
                isClearSelectionEnabled={false}
              />
            )}
          </FieldWithBillableTooltip>
        </div>

        {effectiveDateLabel && (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <InfoIcon size={14} className="shrink-0" />
            {effectiveDateLabel}
          </p>
        )}
      </div>

      {shouldShowRateSourceAlert && (
        <Alert variant="info">
          <AlertDescription className="flex items-start gap-1.5">
            <InfoIcon size={16} className="mt-0.5 shrink-0" />{' '}
            {isInitialRateNonProjectBased
              ? t('form.rateSource.currentDiffers')
              : t('form.rateSource.recommendation')}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
